import React, { useState, useEffect } from 'react';

interface CountResult {
  characters: number;
  charactersNoSpaces: number;
  words: number;
  lines: number;
  paragraphs: number;
  sentences: number;
}

const CharacterCounter: React.FC = () => {
  const [text, setText] = useState('');
  const [counts, setCounts] = useState<CountResult>({
    characters: 0,
    charactersNoSpaces: 0,
    words: 0,
    lines: 0,
    paragraphs: 0,
    sentences: 0
  });

  useEffect(() => {
    calculateCounts(text);
  }, [text]);

  const calculateCounts = (inputText: string) => {
    if (!inputText.trim()) {
      setCounts({
        characters: 0,
        charactersNoSpaces: 0,
        words: 0,
        lines: 0,
        paragraphs: 0,
        sentences: 0
      });
      return;
    }

    const characters = inputText.length;
    const charactersNoSpaces = inputText.replace(/\s/g, '').length;
    const words = inputText.trim().split(/\s+/).filter(word => word.length > 0).length;
    const lines = inputText.split('\n').length;
    const paragraphs = inputText.split(/\n\s*\n/).filter(para => para.trim().length > 0).length;
    const sentences = inputText.split(/[.!?]+/).filter(sentence => sentence.trim().length > 0).length;

    setCounts({
      characters,
      charactersNoSpaces,
      words,
      lines,
      paragraphs,
      sentences
    });
  };

  const handleClear = () => {
    setText('');
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(text);
  };

  const handlePaste = async () => {
    try {
      const clipboardText = await navigator.clipboard.readText();
      setText(clipboardText);
    } catch (err) {
      console.error('Failed to read clipboard:', err);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">字符统计工具</h1>
          <p className="text-lg text-gray-600">快速统计文本的字符数、单词数、行数等信息</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left: Text Input */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-800">输入文本</h2>
                <div className="flex space-x-2">
                  <button
                    onClick={handlePaste}
                    className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors"
                  >
                    粘贴
                  </button>
                  <button
                    onClick={handleClear}
                    className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                  >
                    清空
                  </button>
                  <button
                    onClick={handleCopy}
                    className="px-3 py-2 text-sm bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
                  >
                    复制
                  </button>
                </div>
              </div>
              
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="在此输入或粘贴要统计的文本..."
                className="w-full h-96 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              
              <div className="mt-4 text-sm text-gray-500">
                提示：支持多行文本，会自动统计段落和行数
              </div>
            </div>
          </div>

          {/* Right: Statistics */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-semibold text-gray-800 mb-6">统计结果</h2>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">📊</span>
                    <span className="font-medium text-gray-700">总字符数</span>
                  </div>
                  <span className="text-2xl font-bold text-blue-600">{counts.characters.toLocaleString()}</span>
                </div>

                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">🔤</span>
                    <span className="font-medium text-gray-700">字符数(不含空格)</span>
                  </div>
                  <span className="text-2xl font-bold text-green-600">{counts.charactersNoSpaces.toLocaleString()}</span>
                </div>

                <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">📝</span>
                    <span className="font-medium text-gray-700">单词数</span>
                  </div>
                  <span className="text-2xl font-bold text-purple-600">{counts.words.toLocaleString()}</span>
                </div>

                <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">📄</span>
                    <span className="font-medium text-gray-700">行数</span>
                  </div>
                  <span className="text-2xl font-bold text-orange-600">{counts.lines.toLocaleString()}</span>
                </div>

                <div className="flex items-center justify-between p-4 bg-indigo-50 rounded-lg">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">📚</span>
                    <span className="font-medium text-gray-700">段落数</span>
                  </div>
                  <span className="text-2xl font-bold text-indigo-600">{counts.paragraphs.toLocaleString()}</span>
                </div>

                <div className="flex items-center justify-between p-4 bg-pink-50 rounded-lg">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">💬</span>
                    <span className="font-medium text-gray-700">句子数</span>
                  </div>
                  <span className="text-2xl font-bold text-pink-600">{counts.sentences.toLocaleString()}</span>
                </div>
              </div>

              {/* Additional Info */}
              <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                <h3 className="font-medium text-gray-800 mb-2">统计说明</h3>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• 字符数：包含所有字符（含空格、标点）</li>
                  <li>• 单词数：按空格分割的文本片段</li>
                  <li>• 行数：换行符分隔的行数</li>
                  <li>• 段落数：空行分隔的段落数</li>
                  <li>• 句子数：句号、感叹号、问号分隔的句子数</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="text-3xl mb-3">⚡</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">实时统计</h3>
            <p className="text-gray-600">输入文本时实时显示统计结果，无需手动刷新</p>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="text-3xl mb-3">📋</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">一键操作</h3>
            <p className="text-gray-600">支持粘贴、复制、清空等快捷操作，提高工作效率</p>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="text-3xl mb-3">📊</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">多维度统计</h3>
            <p className="text-gray-600">从字符、单词、行数、段落、句子等多个维度分析文本</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CharacterCounter;
